import { useEffect, useRef } from 'react';

import {
  type SitrepSectionType,
  useRefreshSitrepV2,
} from 'components/DisasterGPT/hooks/useSitrepsV2';
import { isSitrepSectionGenerating } from '../document/sitrepSectionDisplay';

const INTERVAL_MS = 30_000;

export type AutoRefreshConfig = {
  enabled?: boolean;
  intervalMinutes?: number;
};

export function parseAutoRefresh(
  structured: SitrepSectionType['output_format_structured'] | undefined
): AutoRefreshConfig | null {
  const raw = structured?.auto_refresh;
  if (!raw || typeof raw !== 'object') return null;
  const config = raw as AutoRefreshConfig;
  if (!config.enabled) return null;
  const minutes = Number(config.intervalMinutes);
  if (!Number.isFinite(minutes) || minutes <= 0) return null;
  return { enabled: true, intervalMinutes: minutes };
}

function latestVersionTimestamp(section: SitrepSectionType): number | null {
  const version = section.section_versions?.[0];
  const raw = version?.timestamp;
  if (!raw) return null;
  const time = new Date(raw).getTime();
  return Number.isFinite(time) ? time : null;
}

function isGenerating(section: SitrepSectionType): boolean {
  return isSitrepSectionGenerating(section);
}

export function useSectionAutoRefresh(params: {
  sitrepId: string;
  sections: SitrepSectionType[] | undefined;
  lockedSectionIds?: Set<string>;
  enabled?: boolean;
}) {
  const { sitrepId, sections, lockedSectionIds, enabled = true } = params;
  const refreshSitrep = useRefreshSitrepV2();
  const inFlightRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!enabled || !sitrepId || !sections?.length) return undefined;

    const tick = () => {
      const now = Date.now();
      for (const section of sections) {
        if (section.type !== 'ai') continue;
        if (isGenerating(section)) continue;
        if (lockedSectionIds?.has(section.id)) continue;
        if (inFlightRef.current.has(section.id)) continue;
        const config = parseAutoRefresh(section.output_format_structured);
        if (!config?.intervalMinutes) continue;
        const last = latestVersionTimestamp(section);
        if (last != null && now - last < config.intervalMinutes * 60_000) {
          continue;
        }
        inFlightRef.current.add(section.id);
        refreshSitrep.mutate(
          {
            id: sitrepId,
            selectedDatetime: new Date().toISOString(),
            sections: [section.id],
          },
          {
            onSettled: () => {
              inFlightRef.current.delete(section.id);
            },
          }
        );
      }
    };

    tick();
    const handle = window.setInterval(tick, INTERVAL_MS);
    return () => window.clearInterval(handle);
  }, [enabled, lockedSectionIds, refreshSitrep, sections, sitrepId]);
}
